import { useMutation, useQuery } from "@apollo/client";
import { useState } from "react";
import { useFormik } from "formik";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Table } from "../../../../components/Functional/Table";
import { Button, Modal } from "../../../../components/ui";
import { CREATE_EMAIL as createEmailMutation } from "../../../../graphql/mutation/Email";
import { FETCH_EMAILS as fetchEmails } from "../../../../graphql/query/Email";
import { useEntities } from "../../../../hooks/useEntities";
import Actions from "./components/Actions";
import CreateMailingList from "./components/CreateMailingList";
import useUser, { USER_ROLES } from "../../../../hooks/useUser";
import { ResponsivePrimaryNav } from "../../../../components/ui/ResponsivePrimaryNav";

import { FETCH_EMAIL_USAGE } from "../Usage/query";

const Body = () => {
  const navigate = useNavigate();
  const { hasPermission, hasRole } = useUser({ withRoles: true });
  const isAdministrator =
    hasRole(USER_ROLES.ADMIN) || hasRole(USER_ROLES.SUPER_ADMIN);
  const [searchParams] = useSearchParams();
  const [ownerScope, setOwnerScope] = useState(
    searchParams.get("owner") || "mine",
  );
  const { data: usageData, error: usageError } = useQuery(FETCH_EMAIL_USAGE, {
    skip: !isAdministrator,
    fetchPolicy: "cache-and-network",
    pollInterval: 30000,
  });
  const usage = usageData?.emailUsage || [];
  const [createEmail, { client }] = useMutation(createEmailMutation());
  const { entities } = useEntities();
  const formik = useFormik({
    initialValues: { active: null },
  });

  const tabProps = {
    onChange: name => {
      name === "all"
        ? formik?.setValues({ status: null, offset: 0 })
        : formik?.setValues({ status: name, offset: 0 });
    },
    tabs: [
      { label: "All", name: "all" },
      { label: "Draft", name: "draft" },
      { label: "Sent", name: "sent" },
      { label: "Failed", name: "failed" },
      { label: "Archived", name: "archived" },
    ],
    active: formik?.values?.status || "all",
  };

  const tableProps = {
    variant: "dynamicHeight",
    dataKey: "emails",
    headers: [
      {
        label: "Title",
        name: "title",
        sortKey: "title",
        type: "label",
        width: "35%",
        widthInPx: null,
        rowClass: "max-w-0 !pl-0",
        labelClass: "truncate",
      },
      { label: "Sent To", name: "meta.recipient", type: "label" },
      ...(isAdministrator
        ? [{ label: "Owner", name: "ownerEmail", type: "label" }]
        : []),
      {
        label: "Sent By",
        name: "meta.sender",
        sortKey: "meta.sender",
        type: "label",
      },
      {
        label: "Updated At",
        name: "updatedAt",
        sortKey: "updatedAt",
        type: "date",
      },
      { label: "Status", name: "status", sortKey: "status", type: "tag" },
      { label: "Action", name: "action", type: "custom", component: Actions },
    ],
    inputs: [
      { name: "title", label: "Title", type: "text" },
      {
        name: "sentBy",
        label: "Sent By",
        type: "multi-select",
        options:
          entities?.edges?.map(item => {
            return { label: item.name, value: item.name };
          }) || [],
      },
      {
        name: "status",
        label: "Status",
        type: "multi-select",
        options: [
          { label: "Draft", value: "draft" },
          { label: "Sent", value: "sent" },
          { label: "Scheduled", value: "scheduled" },
          { label: "Sending", value: "sending" },
          { label: "Failed", value: "failed" },
        ],
      },
      { name: "from", label: "From", type: "date" },
      { name: "to", label: "To", type: "date" },
    ],
    FETCH_QUERY: fetchEmails({ owner: isAdministrator }),
    tabProps,
    headerActions: isAdministrator && (
      <div>
        <label className="flex items-center justify-end gap-3">
          Mailings to display
          <select
            aria-label="Mailing owner"
            value={ownerScope}
            onChange={event => setOwnerScope(event.target.value)}
            className="rounded border border-gray-300"
          >
            <option value="mine">My mailings</option>
            <option value="all">Everyone’s mailings</option>
            {usage
              .filter(owner => owner.userId != null)
              .map(owner => (
                <option key={owner.userId} value={owner.userId}>
                  {owner.ownerName ||
                    owner.ownerEmail ||
                    `User ${owner.userId}`}
                </option>
              ))}
          </select>
        </label>
        {usageError && <p role="alert">Unable to load email usage.</p>}
      </div>
    ),
    variables: {
      ...formik?.values,
      ...(isAdministrator && ownerScope !== "mine"
        ? {
            allUsers: true,
            ...(ownerScope !== "all" ? { userId: Number(ownerScope) } : {}),
          }
        : {}),
    },
  };

  const handleCreate = () => {
    createEmail({
      variables: {
        input: {
          title: "",
        },
      },
    }).then(resp => {
      const {
        data: { createEmail: { success, email } = {} },
      } = resp || {};
      if (success) {
        // TODO: handle this through updating cache to improve performance
        client.resetStore();
        navigate(`/email/${email?.id}/content`);
      }
    });
  };

  return (
    <div className="min-w-0 flex-1">
      <ResponsivePrimaryNav>
        <div className="flex flex-1 justify-end">
          <div className="flex flex-wrap gap-3">
            {hasPermission("CREATE_MAILING_LIST") && (
              <Modal
                title="New Mailing List"
                scale="sm"
                body={CreateMailingList}
              >
                <Button label="New Mailing List" />
              </Modal>
            )}
            <Button label="New Email" onClick={handleCreate} />
          </div>
        </div>
      </ResponsivePrimaryNav>
      <main className="mt-6 w-full overflow-x-auto">
        <Table key={ownerScope} {...tableProps} />
      </main>
    </div>
  );
};

const ListView = props => {
  return (
    <div className="flex">
      <Body />
    </div>
  );
};

export default ListView;
